import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import {
  getOnboardingState,
  getAuthState,
  getTenantState,
  getEcapState,
} from '../../../store/selectors';
import { getEcap } from '../../../store/actions';
import ECAPCareerCard from './ECAPCareerCard';
import SwitchCareerIndicator from './SwitchCareerIndicator';
import Instructions from './Instructions';
import Loading from '../../utility/Loading';
import { middle } from '../../../copy/ecap/classesSetupCopy.json';
import { coreSubjectsEn } from '../../../copy/ecap/classesSetupStrings';
import { progressBarsStatus } from './drag-drop/dragDropUtil';

function ClassesSubheader(props) {
  const { degree, career, placedClasses } = props;
  const [currentCareer, setCurrentCareer] = useState(null);
  const [currentDegree, setCurrentDegree] = useState(null);

  const isInitialMount = useRef(true);

  const { t } = useTranslation();

  const getClassesPlaced = () => {
    let masterProgress = 0;

    Object.values(coreSubjectsEn).map((subject) => {
      const result = progressBarsStatus(
        props.placedClasses,
        subject,
        props.progressByDegree
      );
      masterProgress += result;
      return masterProgress;
    });

    return masterProgress;
  };

  const classesRequired =
    props.progressByDegree.requiredClassesByAcadPlan.length;

  const addNewScreenReaderMessage = () => {
    const srMessage = document.getElementById('screen-reader-message');
    srMessage.append(
      `Current career: ${
        props.career.titleEn
      }. You have placed ${getClassesPlaced()} of ${classesRequired} classes for this path.`
    );
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      addNewScreenReaderMessage();
    }
  }, [career]);

  useEffect(() => {
    if (!isEmpty(degree) && !isEmpty(career) && placedClasses) {
      setCurrentDegree(degree);
      setCurrentCareer(career);
    }
  }, [degree, career]);

  // Use this for switching through the top3 careers
  const chevronClickHandlerParent = async (direction) => {
    const srMessage = document.getElementById('screen-reader-message');
    srMessage.innerHTML = '';
    let chevronCareerIndex = props.careerIndex;
    switch (direction) {
      case 'left':
        if (chevronCareerIndex > 1) --chevronCareerIndex;
        else if (chevronCareerIndex === 1) chevronCareerIndex = 3;
        break;
      case 'right':
        if (chevronCareerIndex < 3) ++chevronCareerIndex;
        else if (chevronCareerIndex === 3) chevronCareerIndex = 1;
        break;
      default:
    }
    await props.setCareerIndex(chevronCareerIndex);
  };

  if (!isEmpty(currentCareer) && !isEmpty(currentDegree)) {
    return (
      <div className="ecap-career-card-instructions-container">
        <div
          id="screen-reader-message"
          aria-live="assertive"
          className="visually-hidden"
          aria-atomic="true"
          aria-relevant="text additions"
          role="alert"
        />
        <div className="ecap-career-card-switch-indicator-container">
          <div className="ecap-career-card-switch-indicator">
            <ECAPCareerCard
              tenant={props.tenant}
              career={currentCareer}
              degreeTitle={
                currentDegree === null ? null : currentDegree.titleEn
              }
              progressByDegree={props.progressByDegree}
              placedClasses={props.placedClasses}
            />
            <SwitchCareerIndicator
              fillColor={props.tenant.secondary}
              currentCareer={props.career.titleEn}
              chevronClickHandlerParent={(direction) =>
                chevronClickHandlerParent(direction)
              }
              classesRequired={classesRequired}
              classesPlaced={getClassesPlaced()}
            />
          </div>
        </div>
        <Instructions
          textToDisplay={t(
            'Welcome to Education and Career Action Plan (ECAP). Below is a tool for you to plan coursework that will align with your educational and career aspirations. Start by adding courses you have already taken in your academic career then continue adding the courses you plan to take in the upcoming year(s).'
          )}
        />
      </div>
    );
  }

  return <Loading height="100vh" />;
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const auth = getAuthState(state);
  const tenant = getTenantState(state);
  const ecap = getEcapState(state);

  return { onboarding, auth, tenant, ecap };
};

export default connect(mapStateToProps, { getEcap })(ClassesSubheader);
